import { createPath, NavigationType, type DataRouter, type Location } from "react-router";
import { readThread } from "../../api/threads/threadsApi";
import { ROUTE_PATTERNS } from "../../shared/routing/appPaths";
import { readStoredString, writeStoredString } from "../../shared/storage/storedString";
import { sectionOfPath } from "../appSections";
import { subjectRoute, translateLegacyLink } from "./legacyLinks";
import { readRecentlyClosed, rememberClosed, takeClosed, writeRecentlyClosed } from "./recentlyClosed";
import {
  activeTab, addTabNextTo, addTabToActiveGroup, closedTabOf, deleteTab, freshLayout, movedBeside, movedTo, newestTab,
  oneGroupShown, othersInGroup, restoredLayout, selectTab, showBeside, sidebarShown, tabById, tabPlace, tabShowing, tabsOf, titled,
  type LayoutAction, type TabLayoutModel, type TabNode,
} from "./tabs/tabLayout";
import { pathParts, pathnameOf } from "./workspaceAddress";
import type { AppPath, Placement, TabId, Workspace, WorkspaceSnapshot } from "./workspaceTypes";

/** The window: its one browser router, and which of its paths are the workspace's (F90, WA-10). */
export interface WorkspaceWindow {
  readonly router: DataRouter;
  /** Whether `path` is a workspace page, not one of the window's own (signing in, help). */
  readonly isWorkspacePath: (path: AppPath) => boolean;
}

/** A tab's draft guard: runs `close` once its unsaved drafts are saved or dropped. */
export type CloseGuard = (close: () => void) => void;

/**
 * The window history entry's state: which tab the entry belongs to (F87, Back across tabs), and
 * its place in the order the owner wrote entries, so a Back a draft guard stops can be undone.
 */
interface WindowEntryState {
  readonly dmwTab: TabId;
  readonly dmwEntry: number;
}

/** Where the window's history shows the workspace: a tab, its route, and the entry's place. */
interface ShownEntry {
  readonly tab: TabId;
  readonly path: AppPath;
  readonly entry: number;
}

const START_PAGE: AppPath = ROUTE_PATTERNS.threads;
const SAVE_DELAY_MS = 400;

function entryStateOf(location: Location): Partial<WindowEntryState> {
  const state = location.state as Partial<WindowEntryState> | null;
  return {
    ...(typeof state?.dmwTab === "string" ? { dmwTab: state.dmwTab } : {}),
    ...(typeof state?.dmwEntry === "number" ? { dmwEntry: state.dmwEntry } : {}),
  };
}

/** A tab router's matched routes, as `useMatches` gives them in a page. */
function matchesOf(router: DataRouter | undefined): WorkspaceSnapshot["activeMatches"] {
  return router?.state.matches.map(({ route }) => ({ route: { id: route.id }, handle: route.handle as unknown })) ?? [];
}

/**
 * The workspace owner (WA-10, F90; built provisionally, awaiting acceptance). It places every
 * page in a tab, and is the one place where the tab layout, the tabs' own routers and the
 * window's history meet:
 * - each mounted tab has a memory router; what it shows is written back to its tab;
 * - the window's address is the active tab's route, and every move inside a tab or to another
 *   tab is one entry in the window's history, so Back returns to exactly the previous place;
 * - the layout is saved per person in this browser, and restored on start.
 */
export class WorkspaceController {
  readonly model: TabLayoutModel;
  private readonly routers = new Map<TabId, DataRouter>();
  private readonly guards = new Map<TabId, CloseGuard>();
  private readonly unmountedMoves = new Map<TabId, AppPath>();
  private readonly listeners = new Set<() => void>();
  /** The window history entry last written or followed: what the address shows. */
  private shown: ShownEntry | null = null;
  /** The place of the last entry the owner wrote; new entries count on from it. */
  private lastEntry: number;
  /** A Back or Forward the tab hasn't followed yet: if its draft guard keeps the page, the step is undone. */
  private pendingStep: { readonly tab: TabId; readonly back: boolean } | null = null;
  private snapshot: WorkspaceSnapshot;
  private narrow = false;
  private attached = false;
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private recentlyClosed: WorkspaceSnapshot["recentlyClosed"] = [];
  /** The second route of an old split or `beside` link, opened beside once on start. */
  private pendingSide: AppPath | { readonly subjectOfThread: string } | null = null;

  constructor(private readonly browser: WorkspaceWindow, private readonly storageKey: string) {
    const { pathname, search, hash } = browser.router.state.location;
    this.lastEntry = entryStateOf(browser.router.state.location).dmwEntry ?? 0;
    const legacy = translateLegacyLink({ pathname, search, hash });
    const start = legacy?.main ?? createPath({ pathname, search, hash });
    if (legacy?.kind === "pair" && legacy.side !== null) this.pendingSide = legacy.side;
    if (legacy?.kind === "subjectOfThread") this.pendingSide = { subjectOfThread: legacy.threadId };

    const saved = readStoredString(this.storageKey);
    const restored = restoredLayout(saved);
    this.recentlyClosed = restored === null ? [] : readRecentlyClosed(saved);
    this.model = restored ?? freshLayout(start);
    if (restored !== null) {
      // A link opens its page as a tab in the restored layout; a tab already on it is focused.
      const open = tabShowing(this.model, start);
      if (open !== null) {
        this.model.doAction(selectTab(open.getId()));
      } else {
        const added = addTabToActiveGroup(this.model, start);
        if (added !== null) this.model.doAction(added.action);
      }
    }
    this.snapshot = this.readSnapshot();
  }

  // ---- React's side: subscribe, read, attach -------------------------------------------

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  readonly getSnapshot = (): WorkspaceSnapshot => this.snapshot;

  /** Starts following the layout and the window's history; the returned function stops it. */
  attach(): () => void {
    this.attached = true;
    const onModel = () => { this.layoutChanged(); };
    this.model.addChangeListener(onModel);
    const stopWindow = this.browser.router.subscribe((state) => {
      if (state.historyAction === NavigationType.Pop) this.followWindow(state.location);
    });
    const flush = () => { this.save(); };
    window.addEventListener("pagehide", flush);
    const tab = activeTab(this.model);
    if (tab !== null) this.showInWindow(tab.getId(), this.routeOf(tab), true);
    this.openPendingSide();
    return () => {
      this.attached = false;
      this.model.removeChangeListener(onModel);
      stopWindow();
      window.removeEventListener("pagehide", flush);
      this.save();
    };
  }

  /** The `Workspace` a component in tab `tab` gets. */
  workspaceOf(tab: TabId, isActive: boolean): Workspace {
    return {
      tab,
      isActive,
      open: (route, options) => { this.open(tab, route, options?.placement ?? "active", options?.replace === true); },
      close: () => { this.close(tab); },
      setTitle: (title) => { this.setTitle(tab, title); },
      confirmLeave: (then) => { this.requestCloseAll(then); },
      back: (fallback) => { this.back(tab, fallback); },
    };
  }

  // ---- The tabs' side -------------------------------------------------------------------

  /** A tab's router is mounted: from now on, where it goes is the tab's route. The workspace moved it before (`/threads`, F108): the router follows, in place. */
  mountTab(id: TabId, router: DataRouter): () => void {
    this.routers.set(id, router);
    const placed = this.unmountedMoves.get(id);
    this.unmountedMoves.delete(id);
    let shownKey: string | null = null;
    let blocked = false;
    const report = () => {
      const { location, historyAction, navigation, blockers } = router.state;
      const states = [...blockers.values()].map((blocker) => blocker.state);
      if (states.includes("blocked")) {
        blocked = true;
        return;
      }
      // Proceeding, the guard let the step through: the move that follows is reported as usual.
      // Unblocked without a move, the guard kept the page.
      if (blocked && !states.includes("proceeding") && location.key === shownKey) this.tabKeptItsPage(id);
      blocked = false;
      if (navigation.state !== "idle" || location.key === shownKey) return;
      shownKey = location.key;
      this.tabNavigated(id, location, historyAction);
    };
    const stop = router.subscribe(report);
    if (placed !== undefined && placed !== createPath(router.state.location)) void router.navigate(placed, { replace: true });
    else report();
    return () => {
      stop();
      if (this.routers.get(id) === router) this.routers.delete(id);
    };
  }

  guardTab(id: TabId, guard: CloseGuard): () => void {
    this.guards.set(id, guard);
    return () => { if (this.guards.get(id) === guard) this.guards.delete(id); };
  }

  setTitle(id: TabId, title: string | null): void {
    const tab = tabById(this.model, id);
    const action = tab === null ? null : titled(tab, title);
    if (action !== null) this.model.doAction(action);
  }

  setSidebarOpen(id: TabId, open: boolean): void {
    const tab = tabById(this.model, id);
    const action = tab === null ? null : sidebarShown(tab, open);
    if (action !== null) this.model.doAction(action);
  }

  /** The user's actions in the layout pass here first: a tab's × asks its draft guard, which never drops a draft. */
  readonly intercept = (action: LayoutAction): LayoutAction | undefined => {
    const closed = closedTabOf(action);
    if (closed === null) return action;
    this.close(closed);
    return undefined;
  };

  // ---- Commands -------------------------------------------------------------------------

  open(from: TabId, route: AppPath, placement: Placement, replace = false): void {
    if (!this.browser.isWorkspacePath(route)) {
      this.leaveFor(route, false, null);
      return;
    }
    const tab = tabById(this.model, from) ?? activeTab(this.model);
    if (tab === null) return;
    switch (placement) {
      case "active":
        this.navigateTab(tab, route, replace);
        break;
      case "newTab": {
        const added = addTabNextTo(this.model, tab, route);
        if (added !== null) this.model.doAction(added.action);
        break;
      }
      case "beside": {
        const action = showBeside(this.model, tab, route, this.narrow);
        if (action !== null) this.model.doAction(action);
        break;
      }
    }
  }

  /** A section in the top bar (Q3): the newest tab of that section, or a new one; `newTab` always opens one. */
  openSection(path: AppPath, newTab: boolean): void {
    const section = sectionOfPath(pathnameOf(path));
    const newest = newTab ? null : newestTab(this.model, (route) => sectionOfPath(pathnameOf(route)) === section);
    if (newest !== null) {
      this.model.doAction(selectTab(newest.getId()));
      return;
    }
    const added = addTabToActiveGroup(this.model, path);
    if (added !== null) this.model.doAction(added.action);
  }

  /** Every open tab in layout order, with its name: the page's title, or its route. */
  listTabs(): readonly { readonly id: TabId; readonly name: string; readonly active: boolean }[] {
    const active = activeTab(this.model);
    return tabsOf(this.model).map((tab) => ({ id: tab.getId(), name: tab.getName(), active: tab === active }));
  }

  selectTab(id: TabId): void {
    this.model.doAction(selectTab(id));
  }

  /** Closes `id` once its draft guard lets it go, then runs `then`; a draft kept stops there. */
  close(id: TabId, then?: () => void): void {
    // The model's own actions don't pass `intercept`: this one closes it.
    const remove = () => {
      const tab = tabById(this.model, id);
      if (tab !== null) this.recentlyClosed = rememberClosed(this.recentlyClosed, { route: this.routeOf(tab), name: tabPlace(tab)?.title ?? this.routeOf(tab) });
      this.model.doAction(deleteTab(id));
      then?.();
    };
    const guard = this.guards.get(id);
    if (guard === undefined) remove();
    else guard(remove);
  }

  /** Restores a closed route in the group whose chooser was used. */
  reopenClosed(index: number, from: TabId): void {
    const [closed, remaining] = takeClosed(this.recentlyClosed, index);
    if (closed === undefined || tabById(this.model, from) === null) return;
    this.recentlyClosed = remaining;
    this.open(from, closed.route, "newTab");
  }

  /** The tab menu's "Close others": the group's other tabs, one after another, each through its guard. */
  closeOthers(id: TabId): void {
    const tab = tabById(this.model, id);
    const others = tab === null ? [] : othersInGroup(tab);
    others.reduceRight<() => void>((next, other) => () => { this.close(other, next); }, () => undefined)();
  }

  /** The browser's Back, from a page's own control (F87); entry 0 is the first this workspace wrote, so nothing of DMW lies before it. */
  back(id: TabId, fallback: AppPath): void {
    if ((this.shown?.entry ?? 0) > 0) {
      void this.browser.router.navigate(-1);
      return;
    }
    const tab = tabById(this.model, id);
    if (tab !== null) this.navigateTab(tab, fallback, true);
  }

  /** The tab menu's "Move to the side". */
  moveBeside(id: TabId): void {
    const tab = tabById(this.model, id);
    const action = tab === null ? null : movedBeside(this.model, tab);
    if (action !== null) this.model.doAction(action);
  }

  /** The chrome's links and commands act on the active tab, as a link in it does; the window's own pages leave the workspace. */
  navigateActive(path: AppPath, replace: boolean, state: unknown): void {
    if (!this.browser.isWorkspacePath(path)) {
      this.leaveFor(path, replace, state);
      return;
    }
    const tab = activeTab(this.model);
    if (tab !== null) this.navigateTab(tab, path, replace, state);
  }

  /** Leaving the workspace (signing out, the window's own pages): each tab's draft guard but `asked`'s asks in turn, then `close` runs. */
  requestCloseAll(close: () => void, asked: TabId | null = null): void {
    const guards = [...this.guards].filter(([tab]) => tab !== asked).map(([, guard]) => guard);
    guards.reduceRight<() => void>((next, guard) => () => { guard(next); }, close)();
  }

  setNarrow(narrow: boolean): void {
    this.narrow = narrow;
    this.showOneGroupWhenNarrow();
  }

  // ---- Private --------------------------------------------------------------------------

  private navigateTab(tab: TabNode, path: AppPath, replace: boolean, state: unknown = null): void {
    const router = this.routers.get(tab.getId());
    if (router !== undefined) {
      if (createPath(router.state.location) !== path) void router.navigate(path, { replace, state });
    } else if (tabPlace(tab)?.route !== path) {
      // Not mounted yet: it mounts on this route when it shows, or its router follows (mountTab).
      this.unmountedMoves.set(tab.getId(), path);
      this.model.doAction(movedTo(tab, path));
    }
  }

  private tabNavigated(id: TabId, location: Location, historyAction: NavigationType): void {
    const path = createPath(location);
    if (!this.browser.isWorkspacePath(path)) {
      // A link to the window's own pages (help): the tab stays where it was; its own guard asked already.
      void this.routers.get(id)?.navigate(-1);
      this.leaveFor(path, false, location.state, id);
      return;
    }
    const tab = tabById(this.model, id);
    if (tab === null) return;
    if (this.pendingStep?.tab === id) this.pendingStep = null;
    // The window first: the layout change that follows then finds the address already there.
    if (activeTab(this.model)?.getId() === id) this.showInWindow(id, path, historyAction !== NavigationType.Push);
    if (tabPlace(tab)?.route !== path) this.model.doAction(movedTo(tab, path));
    this.refresh();
  }

  private layoutChanged(): void {
    if (tabsOf(this.model).length === 0) {
      // Closing the last tab opens the start page, the Threads inbox.
      queueMicrotask(() => {
        const added = addTabToActiveGroup(this.model, START_PAGE);
        if (added !== null && tabsOf(this.model).length === 0) this.model.doAction(added.action);
      });
      return;
    }
    // Another tab became the one worked in: one entry in the window's history.
    const tab = activeTab(this.model);
    if (tab !== null && tab.getId() !== this.shown?.tab) this.showInWindow(tab.getId(), this.routeOf(tab), false);
    if (this.narrow) this.showOneGroupWhenNarrow();
    this.refresh();
    this.scheduleSave();
  }

  /** Back and Forward: the entry names its tab; that tab shows, on the entry's route. A closed tab opens again. */
  private followWindow(location: Location): void {
    const path = createPath(location);
    if (!this.browser.isWorkspacePath(path)) return;
    const { dmwTab, dmwEntry } = entryStateOf(location);
    const back = dmwEntry !== undefined && this.shown !== null && dmwEntry < this.shown.entry;
    const tab = tabById(this.model, dmwTab);
    if (tab !== null) {
      this.shown = { tab: tab.getId(), path, entry: dmwEntry ?? this.lastEntry };
      if (activeTab(this.model) !== tab) this.model.doAction(selectTab(tab.getId()));
      if (this.routers.has(tab.getId()) && this.routeOf(tab) !== path) this.pendingStep = { tab: tab.getId(), back };
      this.navigateTab(tab, path, true);
      return;
    }
    // A closed tab opens again, as itself, so its other entries find it.
    const added = addTabToActiveGroup(this.model, path, dmwTab);
    if (added === null) return;
    this.shown = { tab: added.id, path, entry: dmwEntry ?? this.lastEntry };
    this.model.doAction(added.action);
    if (dmwTab === undefined) this.writeEntry(added.id, path, true);
  }

  /** The tab's draft guard kept its page against a Back or Forward: the window steps back to where it was. */
  private tabKeptItsPage(id: TabId): void {
    const step = this.pendingStep;
    if (step?.tab !== id) return;
    this.pendingStep = null;
    void this.browser.router.navigate(step.back ? 1 : -1);
  }

  /** Leaves the workspace for one of the window's own pages once every tab's drafts are saved or dropped; signing out asked before. */
  private leaveFor(path: AppPath, replace: boolean, state: unknown, asked: TabId | null = null): void {
    const leave = () => { void this.browser.router.navigate(path, { replace, state }); };
    if (pathnameOf(path) === ROUTE_PATTERNS.login) leave();
    else this.requestCloseAll(leave, asked);
  }


  private showInWindow(id: TabId, path: AppPath, replace: boolean): void {
    if (this.shown?.tab === id && this.shown.path === path) return;
    this.writeEntry(id, path, replace);
  }

  private writeEntry(id: TabId, path: AppPath, replace: boolean): void {
    const entry = replace ? this.shown?.entry ?? this.lastEntry : this.lastEntry + 1;
    this.lastEntry = Math.max(this.lastEntry, entry);
    this.shown = { tab: id, path, entry };
    const state: WindowEntryState = { dmwTab: id, dmwEntry: entry };
    void this.browser.router.navigate(path, { replace, state });
  }

  private routeOf(tab: TabNode): AppPath {
    const router = this.routers.get(tab.getId());
    return router === undefined ? tabPlace(tab)?.route ?? START_PAGE : createPath(router.state.location);
  }

  private showOneGroupWhenNarrow(): void {
    const action = oneGroupShown(this.model, this.narrow);
    if (action !== null) this.model.doAction(action);
  }

  private openPendingSide(): void {
    const side = this.pendingSide;
    this.pendingSide = null;
    const from = activeTab(this.model);
    if (side === null || from === null) return;
    if (typeof side === "string") {
      this.open(from.getId(), side, "beside");
      return;
    }
    // The old `?beside=source` link: the Thread's subject, which only the server knows.
    readThread(side.subjectOfThread).then(
      (thread) => {
        const route = subjectRoute(thread.bindings.subject);
        if (route !== null && this.attached) this.open(from.getId(), route, "beside");
      },
      () => undefined,
    );
  }

  private readSnapshot(): WorkspaceSnapshot {
    const tab = activeTab(this.model);
    const route = tab === null ? START_PAGE : this.routeOf(tab);
    return {
      activeTab: tab?.getId() ?? "",
      activeLocation: pathParts(route),
      activeMatches: matchesOf(tab === null ? undefined : this.routers.get(tab.getId())),
      openRoutes: tabsOf(this.model).map((open) => tabPlace(open)?.route ?? START_PAGE),
      tabNames: tabsOf(this.model).map((open) => open.getName()),
      closedSidebars: tabsOf(this.model).filter((open) => tabPlace(open)?.sidebarClosed === true).map((closed) => closed.getId()),
      recentlyClosed: this.recentlyClosed,
    };
  }

  private refresh(): void {
    const next = this.readSnapshot();
    const current = this.snapshot;
    if (
      next.activeTab === current.activeTab &&
      createPath(next.activeLocation) === createPath(current.activeLocation) &&
      next.activeMatches.map(({ route }) => route.id).join() === current.activeMatches.map(({ route }) => route.id).join() &&
      next.openRoutes.join("\n") === current.openRoutes.join("\n") &&
      next.tabNames.join("\n") === current.tabNames.join("\n") &&
      next.closedSidebars.join() === current.closedSidebars.join()
      && next.recentlyClosed === current.recentlyClosed
    ) {
      return;
    }
    this.snapshot = next;
    for (const listener of this.listeners) listener();
  }

  private scheduleSave(): void {
    if (this.saveTimer !== null) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => { this.save(); }, SAVE_DELAY_MS);
  }

  private save(): void {
    if (this.saveTimer !== null) clearTimeout(this.saveTimer);
    this.saveTimer = null;
    writeStoredString(this.storageKey, writeRecentlyClosed(this.model.toJson(), this.recentlyClosed));
  }
}
